import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Flex, ScrollArea, Spinner, Text, Em } from "@radix-ui/themes";
import {
  buildTranscript,
  CommentValue,
  TicketTranscriptFilterValue,
} from "@/api/ticket-transcript/TicketTranscript.model";
import { TicketExploreCollaborator } from "@/api/useExplore/Explore.model";
import { buildColorMap } from "@/components/radixColors";

import { TicketTranscriptHeader } from "./TicketTranscriptHeader";
import { Comment } from "./Comment";
import styles from "./TicketTranscript.module.scss";
import { translateText } from "@/services/translation";
import { toast } from "sonner";

interface TicketTranscriptProps {
  data?: CommentValue[];
  isLoading: boolean;
  collaborators?: TicketExploreCollaborator[];
  chooseComment?: (commentId: string, comment: string) => void;
  chooseMessagesUpTo?: (targetCommentId: string) => void;
  onChainOfThoughtsClick?: (comment: CommentValue) => void;
}

export function TicketTranscript({
  data,
  isLoading = false,
  collaborators = [],
  chooseComment,
  chooseMessagesUpTo,
  onChainOfThoughtsClick,
}: TicketTranscriptProps) {
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<TicketTranscriptFilterValue>(
    {}
  );

  // Translation state management
  const [translateAll, setTranslateAll] = useState(false);
  const [translations, setTranslations] = useState<Map<string, string>>(
    new Map()
  );
  const [isBatchTranslating, setIsBatchTranslating] = useState(false);
  const [translatingMessages, setTranslatingMessages] = useState<Set<string>>(
    new Set()
  );
  const [individuallyTranslated, setIndividuallyTranslated] = useState<
    Set<string>
  >(new Set());

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);

  // Find and store reference to scroll container
  useEffect(() => {
    const findScrollContainer = () => {
      // The ScrollArea component creates a scrollable div, let's find it
      const scrollableDiv = document.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement;
      if (scrollableDiv) {
        scrollContainerRef.current = scrollableDiv;
      }
    };

    // Try to find it after a short delay to ensure ScrollArea component has rendered
    const timeout = setTimeout(findScrollContainer, 100);
    return () => clearTimeout(timeout);
  }, []);

  // Helper to preserve scroll position during state changes
  const preserveScrollPosition = useCallback((callback: () => void) => {
    // Save current scroll position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }

    // Execute the callback
    callback();

    // Restore scroll position after React re-renders
    requestAnimationFrame(() => {
      if (scrollContainerRef.current && scrollPositionRef.current > 0) {
        scrollContainerRef.current.scrollTop = scrollPositionRef.current;
      }
    });
  }, []);

  const comments = useMemo(() => {
    if (!data) {
      return [];
    }

    const knownCollaboratorsToColor = buildColorMap(
      collaborators.map((c) => c.name)
    );

    return buildTranscript(data, knownCollaboratorsToColor);
  }, [data, collaborators]);

  const handleTranslateAll = useCallback(
    async (enabled: boolean) => {
      if (!enabled) {
        preserveScrollPosition(() => {
          setTranslateAll(false);
        });
        return;
      }

      preserveScrollPosition(() => {
        setIsBatchTranslating(true);
        setTranslateAll(true);
      });

      // Get untranslated messages
      const untranslated = comments
        .filter((c) => !translations.has(c.body))
        .map((c) => c.body);

      // Translate in parallel with error handling
      const results = await Promise.allSettled(
        untranslated.map((body) =>
          translateText(body).then((translated) => ({ body, translated }))
        )
      );

      // Update cache with successful translations
      preserveScrollPosition(() => {
        setTranslations((prev) => {
          const newMap = new Map(prev);
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              newMap.set(result.value.body, result.value.translated);
            }
          });
          return newMap;
        });
      });

      const failedCount = results.filter((r) => r.status === "rejected").length;
      if (failedCount > 0) {
        toast.error(`Failed to translate ${failedCount} message(s)`);
      }

      setIsBatchTranslating(false);
    },
    [comments, translations, preserveScrollPosition]
  );

  const handleTranslateMessage = useCallback(
    async (messageBody: string, shouldTranslate: boolean) => {
      if (!shouldTranslate) {
        // Remove from individually translated set
        preserveScrollPosition(() => {
          setIndividuallyTranslated((prev) => {
            const newSet = new Set(prev);
            newSet.delete(messageBody);
            return newSet;
          });
        });
        return;
      }

      if (translations.has(messageBody)) {
        // Already translated, just add to individually translated set
        preserveScrollPosition(() => {
          setIndividuallyTranslated((prev) => new Set(prev).add(messageBody));
        });
        return;
      }

      setTranslatingMessages((prev) => new Set(prev).add(messageBody));

      try {
        const translated = await translateText(messageBody);
        preserveScrollPosition(() => {
          setTranslations((prev) => new Map(prev).set(messageBody, translated));
          setIndividuallyTranslated((prev) => new Set(prev).add(messageBody));
        });
      } catch (error) {
        console.error("Translation failed:", error);
        toast.error("Failed to translate message");
      } finally {
        setTranslatingMessages((prev) => {
          const newSet = new Set(prev);
          newSet.delete(messageBody);
          return newSet;
        });
      }
    },
    [translations, preserveScrollPosition]
  );

  const visibleComments = useMemo(() => {
    const testSearch = search.length > 0;
    const testFilter = Object.keys(filterValue).length > 0;

    let filteredComments = comments;

    if (testSearch || testFilter) {
      const searchString = search.toLowerCase();

      filteredComments = comments.filter((comment) => {
        if (testSearch && !comment.body.toLowerCase().includes(searchString)) {
          return false;
        }

        if (testFilter && filterValue.messageType != null) {
          const showInternal = filterValue.messageType.includes("internal");
          const showPublic = filterValue.messageType.includes("public");

          if (comment.isInternal && !showInternal) {
            return false;
          }

          if (!comment.isInternal && !showPublic) {
            return false;
          }
        }

        return true;
      });
    }

    // Apply translations to visible comments
    return filteredComments.map((comment) => {
      const shouldShowTranslation =
        (translateAll || individuallyTranslated.has(comment.body)) &&
        translations.has(comment.body);
      const displayBody = shouldShowTranslation
        ? translations.get(comment.body) ?? comment.body
        : comment.body;

      return {
        ...comment,
        body: displayBody,
        originalBody: comment.body, // Keep original for translation key
      };
    });
  }, [comments, search, filterValue, translateAll, translations, individuallyTranslated]);

  return (
    <Flex className={styles.TicketTranscript}>
      <TicketTranscriptHeader
        search={search}
        filterValue={filterValue}
        onSearchChange={setSearch}
        onFilterChange={setFilterValue}
        translateAll={translateAll}
        onTranslateAll={handleTranslateAll}
        isBatchTranslating={isBatchTranslating}
      />

      <Flex className={styles.CommentList}>
        <ScrollArea
          type={"auto"}
          scrollbars={"vertical"}
          className={styles.ScrollArea}
          style={{ width: "100%" }}
        >
          {isLoading ? (
            <Flex pt={"9"} justify={"center"} align={"center"}>
              <Spinner size={"3"} />
            </Flex>
          ) : (
            <>
              {visibleComments.length === 0 && (
                <Flex
                  pt={"9"}
                  direction={"column"}
                  gap={"1"}
                  justify={"center"}
                  align={"center"}
                >
                  <Text
                    size={"2"}
                    color={"gray"}
                    weight={"medium"}
                    highContrast
                  >
                    No Comments Found
                  </Text>

                  {(search.length > 0 ||
                    Object.keys(filterValue).length > 0) && (
                    <Text size={"2"} color={"gray"}>
                      <Em>Try to change filter or search</Em>
                    </Text>
                  )}
                </Flex>
              )}

              {visibleComments.length > 0 &&
                visibleComments.map((comment, index) => {
                  const previousComment = visibleComments[index - 1];
                  const hideHeader =
                    previousComment?.authorId === comment.authorId;

                  const nextComment = visibleComments[index + 1];
                  const hasConscutiveComment =
                    nextComment?.authorId === comment.authorId;

                  return (
                    <Comment
                      key={comment.externalId}
                      isFirst={index === 0}
                      hideHeader={hideHeader}
                      hasConscutiveComment={hasConscutiveComment}
                      {...comment}
                      suggestedReplies={previousComment?.suggestedReplies}
                      previousCommentId={previousComment?.id}
                      chooseComment={chooseComment}
                      chooseMessagesUpTo={chooseMessagesUpTo}
                      onChainOfThoughtsClick={onChainOfThoughtsClick}
                      translateAll={translateAll}
                      isTranslated={individuallyTranslated.has(
                        comment.originalBody || comment.body
                      )}
                      onTranslate={handleTranslateMessage}
                      isTranslating={translatingMessages.has(
                        comment.originalBody || comment.body
                      )}
                    />
                  );
                })}
            </>
          )}
        </ScrollArea>
      </Flex>
    </Flex>
  );
}
